import React, { useMemo, useState } from 'react';
import { Button, ButtonToolbar, Col, Container, Form, Row } from 'react-bootstrap';
import Cards, { Focused } from 'react-credit-cards-2';
import { useTranslationWithRef } from '../../hooks/useTranslationWithRef';
import { formatCreditCardNumber, formatCVC, formatExpirationDate } from '../../utils/formatCardData';
import { payment } from '../../api/ShoppingCart';
import { APIError } from '../../api/ObservableFromFetch';
import { toast } from 'react-toastify';
import OrderItem from '../../api/models/addOnTesting/ShoppingCart';
import { PaymentApiRequest, PaymentApiResponse } from '../../api/models/addOnTesting/Payment';
import Address from '../../api/models/Address';
import { formatMoney } from '../../formatters/formatMoney';

interface PaymentStepProps {
  address?: Address
  items: OrderItem[]
  testEventId: number
  onBack: () => void
  onNext: (success: boolean) => void
}

interface PaymentStepState
{
    number: string
    name: string
    expiry: string
    cvc: string
    issuer: string
    focused: string
    formData: null
  }

export const PaymentStep = ({ address, items, testEventId, onBack, onNext }: PaymentStepProps) => {
  const [translationRef, t] = useTranslationWithRef()
  //const sortedItems = useMemo(() => items.sort((itemA, itemB) => itemA.name.localeCompare(itemB.name)), [items])
  const total = useMemo(() => items.reduce((sum, item) => sum + item.itemPrice, 0), [items])
  const [saving, setSaving] = useState(false)

  const [cardData, setCardData] = useState<PaymentStepState>({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
    issuer: "",
    focused: "",
    formData: null
  });

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.persist()
    setCardData((prev) => ({ ...prev, focused: e.target.name as Focused }));
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "number") {
      e.target.value = formatCreditCardNumber(e.target.value);
    } else if (e.target.name === "expiry") {
      e.target.value = formatExpirationDate(e.target.value);
    } else if (e.target.name === "cvc") {
      e.target.value = formatCVC(e.target.value);
    }

    const key: keyof PaymentStepState = e.target.name as keyof PaymentStepState
    setCardData({...cardData, [key]: e.target.value ?? ""});
  };

  const pay = () => {
    if (saving) {
      return
    }
    setSaving(true)
    const request: PaymentApiRequest = {
      testEventId: testEventId,
      amount: total,
      cardNumber: cardData.number.replaceAll(" ", ""),
      expirationDate: cardData.expiry,
      cardCode: cardData.cvc,
      cardId: cardData.issuer,
      billingAddress: address,
      //items: items.map((i: OrderItem) => {return { id: (i.addOnTestingPanelId ?? "").toString(), name: i.name, quantity: 1, price: i.itemPrice }})
    }
    payment(request).subscribe({
      next: (result: PaymentApiResponse) => {
        setSaving(false)
        onNext(result.success)
      },
      error: (e: APIError) => {
        setSaving(false)
        toast.error(t(e.message, { data: e.data }))
        onNext(false)
      }
    })
  }

  return (
    <div>
      <Container className="container-fluid">
        <h4>{t("shoppingCart.orderSummary")}</h4>
        <Row key="total" className="fw-bold mb-3">
          <Col xs={6} md={3}>{t("shoppingCart.total")}</Col>
          <Col xs={6} md={3} className="text-end">{formatMoney(total)}</Col>
        </Row>
      </Container>
      <Cards
        number={cardData.number}
        expiry={cardData.expiry}
        cvc={cardData.cvc}
        name={cardData.name}
        focused={cardData.focused as Focused}
        locale = {{
          valid: t("shoppingCart.cardExpiration"),
        }}
        placeholders = {{
          name: t("shoppingCart.placeholderName"),
        }}
      />
      <Form className="my-3 w-md-30 mx-auto">
        <Form.Group className="mb-3" controlId="number">
          <Form.Control type="tel" name="number" placeholder={t("shoppingCart.cardNumber")} pattern="[1-9\| ]{16,22}" required onChange={handleInputChange} onFocus={handleInputFocus}></Form.Control>
        </Form.Group>
        <Form.Group className="mb-3" controlId="name">
          <Form.Control type="text" name="name" placeholder={t("shoppingCart.cardName")} required onChange={handleInputChange} onFocus={handleInputFocus}></Form.Control>
        </Form.Group>
        <Row>
          <Col md={6}>
            <Form.Control type="tel" name="expiry" placeholder={t("shoppingCart.cardExpiration")} pattern="\d\d/\d\d" required onChange={handleInputChange} onFocus={handleInputFocus}></Form.Control>
          </Col>
          <Col md={6}>
            <Form.Control type="tel" name="cvc" placeholder="CVC" pattern="\d{3,4}" required onChange={handleInputChange} onFocus={handleInputFocus}></Form.Control>
          </Col>
        </Row>
        <input type="hidden" name="issuer" value={cardData.issuer} />
      </Form>
      <ButtonToolbar className="justify-content-between">
        <Button variant="secondary" onClick={() => onBack()}>{t("shoppingCart.back")}</Button>
        <Button type="submit" disabled={saving} onClick={() => pay()}>{t("shoppingCart.pay")}</Button>
      </ButtonToolbar>
    </div>
  );
}

export default PaymentStep;
